// Mécanismes : des interrupteurs posés sur des objets de la carte, reliés par des courants à d'autres objets dont ils
// changent l'état. Chaque joueur a ses interrupteurs, qui repartent tous éteints quand il arrive sur la carte (comme les
// rochers poussés reviennent à leur place). Partagé par le serveur (collisions), le jeu (prédiction, rendu) et
// l'administration (outil Mécanismes de l'éditeur de cartes).
import { coveredCells, type MapElement, type MapObject } from './map-elements.ts';
import type { Position } from './protocol.ts';

/** Comment le joueur active un interrupteur : en marchant sur l'une de ses cases, ou face à lui avec la touche d'action. */
export type SwitchTrigger = 'step' | 'interact';
export const SWITCH_TRIGGERS: Readonly<Record<SwitchTrigger, string>> = { step: 'En marchant dessus', interact: 'En interagissant (touche d\'action)' };
export const isSwitchTrigger = (value: unknown): value is SwitchTrigger => typeof value === 'string' && Object.hasOwn(SWITCH_TRIGGERS, value);

/**
 * Ce que fait l'état actif à l'objet : `hitbox` inverse sa hitbox (une barrière s'ouvre, un objet traversable se met à
 * bloquer) ; `relay` ne change rien à l'objet, qui ne fait que transmettre son état par ses propres courants. Dans les deux
 * cas, ses calques joués « Au déclenchement » suivent l'état (première image : inactif ; dernière : actif).
 */
export type MechanismEffect = 'hitbox' | 'relay';
export const MECHANISM_EFFECTS: Readonly<Record<MechanismEffect, string>> = { hitbox: 'Hitbox + animation', relay: 'Relais (transmet seulement)' };
export const isMechanismEffect = (value: unknown): value is MechanismEffect => typeof value === 'string' && Object.hasOwn(MECHANISM_EFFECTS, value);

/** Comment se combinent les courants qui arrivent sur un objet : un seul suffit, tous, ou chacun inverse l'état. */
export type MechanismCombine = 'any' | 'all' | 'toggle';
export const MECHANISM_COMBINES: Readonly<Record<MechanismCombine, string>> = {
  any: 'Un seul courant suffit', all: 'Tous les courants', toggle: 'Chaque courant inverse l\'état',
};
export const isMechanismCombine = (value: unknown): value is MechanismCombine => typeof value === 'string' && Object.hasOwn(MECHANISM_COMBINES, value);

/** Courant vers l'objet `to` (identifiant de son mécanisme) ; inversé, il porte l'inverse de l'état de sa source. */
export interface MechanismLink {
  to: string;
  inverted: boolean;
}

/**
 * Mécanisme d'un objet posé. Un interrupteur (`trigger`) a son propre état, qui bascule à chaque activation ; les autres
 * prennent l'état que leur donnent leurs courants entrants (`combine`), inactifs sans courant. Tous transmettent leur
 * état par leurs courants sortants (`links`).
 */
export interface ObjectMechanism {
  /** Identifiant stable, unique dans la carte (les courants s'y rattachent). */
  id: string;
  /** Interrupteur : comment on l'active ; null : objet commandé par ses courants entrants. */
  trigger: SwitchTrigger | null;
  effect: MechanismEffect;
  combine: MechanismCombine;
  links: MechanismLink[];
}

export const MECHANISM_ID_PATTERN = /^[a-z0-9-]{4,40}$/;
export const MECHANISM_LIMITS = { maxLinks: 64 } as const;

export const newMechanism = (trigger: SwitchTrigger | null = null): ObjectMechanism => ({
  id: `m-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`, trigger, effect: trigger ? 'relay' : 'hitbox', combine: 'any', links: [],
});

/** Courants entrants de chaque mécanisme : identifiant → (source, inversé). */
function incomingLinks(objects: readonly Pick<MapObject, 'mechanism'>[]): Map<string, { from: string; inverted: boolean }[]> {
  const incoming = new Map<string, { from: string; inverted: boolean }[]>();
  for (const { mechanism } of objects) {
    if (!mechanism) continue;
    for (const link of mechanism.links) {
      const list = incoming.get(link.to);
      const entry = { from: mechanism.id, inverted: link.inverted };
      if (list) list.push(entry); else incoming.set(link.to, [entry]);
    }
  }
  return incoming;
}

/**
 * État (actif ou non) de chaque objet à mécanisme, avec ces interrupteurs allumés (`on` : identifiants). Une boucle de
 * relais (refusée à l'enregistrement) compte comme inactive.
 */
export function mechanismStates<T extends Pick<MapObject, 'mechanism'>>(objects: readonly T[], on: ReadonlySet<string>): Map<T, boolean> {
  const byId = new Map<string, ObjectMechanism>();
  for (const { mechanism } of objects) if (mechanism) byId.set(mechanism.id, mechanism);
  const incoming = incomingLinks(objects);
  const memo = new Map<string, boolean>();
  const visiting = new Set<string>();
  const stateOf = (id: string): boolean => {
    const known = memo.get(id);
    if (known !== undefined) return known;
    const mechanism = byId.get(id);
    if (!mechanism || visiting.has(id)) return false;
    visiting.add(id);
    let state: boolean;
    if (mechanism.trigger) state = on.has(id);
    else {
      const inputs = (incoming.get(id) ?? []).filter((link) => byId.has(link.from)).map((link) => stateOf(link.from) !== link.inverted);
      state = !inputs.length ? false
        : mechanism.combine === 'all' ? inputs.every(Boolean)
          : mechanism.combine === 'any' ? inputs.some(Boolean)
            : inputs.reduce((acc, input) => acc !== input, false);
    }
    visiting.delete(id);
    memo.set(id, state);
    return state;
  };
  const states = new Map<T, boolean>();
  for (const object of objects) if (object.mechanism) states.set(object, stateOf(object.mechanism.id));
  return states;
}

/** Objets dont la hitbox est inversée : mécanisme à effet « hitbox », actif. */
export function flippedHitboxes<T extends Pick<MapObject, 'mechanism'>>(objects: readonly T[], on: ReadonlySet<string>): Set<T> {
  const flipped = new Set<T>();
  if (!objects.some((object) => object.mechanism?.effect === 'hitbox')) return flipped;
  for (const [object, active] of mechanismStates(objects, on)) if (active && object.mechanism?.effect === 'hitbox') flipped.add(object);
  return flipped;
}

/** Interrupteurs activés par `trigger` qui couvrent cette case (identifiants). */
export function switchesAt(objects: readonly MapObject[], elements: ReadonlyMap<number, MapElement>, { x, y }: Position, trigger: SwitchTrigger): string[] {
  const ids: string[] = [];
  for (const object of objects) {
    const element = elements.get(object.elementId);
    if (!element || object.mechanism?.trigger !== trigger) continue;
    for (const cell of coveredCells(object, element)) if (cell.x === x && cell.y === y) { ids.push(object.mechanism.id); break; }
  }
  return ids;
}

/** Bascule ces interrupteurs (allumé ↔ éteint) ; renvoie vrai si quelque chose a changé. */
export function flipSwitches(on: Set<string>, flipped: readonly string[]): boolean {
  for (const id of flipped) if (!on.delete(id)) on.add(id);
  return flipped.length > 0;
}

/** Courant qui forme une boucle (un relais qui finit par se commander lui-même), ou null. */
export function mechanismCycle(objects: readonly Pick<MapObject, 'mechanism'>[]): string | null {
  const byId = new Map<string, ObjectMechanism>();
  for (const { mechanism } of objects) if (mechanism) byId.set(mechanism.id, mechanism);
  const state = new Map<string, 'visiting' | 'done'>();
  const visit = (id: string): string | null => {
    if (state.get(id) === 'done') return null;
    if (state.get(id) === 'visiting') return id;
    state.set(id, 'visiting');
    for (const link of byId.get(id)?.links ?? []) {
      // Un interrupteur ne prend pas l'état de ses courants entrants : il coupe la boucle.
      if (byId.get(link.to)?.trigger) continue;
      const found = visit(link.to);
      if (found) return found;
    }
    state.set(id, 'done');
    return null;
  };
  for (const id of byId.keys()) {
    const found = visit(id);
    if (found) return found;
  }
  return null;
}
